import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePlans, useUserSubscriptions } from "../../api/hooks/usePlans";
import { useCreateSetupIntent } from "../../api/hooks/usePayment";
import { Subscription } from "../../types/plans";
import PricingCard from "./PricingCard";
import AgentAgencySwitch from "./AgentAgencySwitch";
import "./Pricing.css";
import { useAuth } from "../../context/AuthContext";

const Pricing: React.FC = () => {
  const navigate = useNavigate();
  const [isAgent, setIsAgent] = useState(true);
  const { isAuthenticated, account } = useAuth();
  const { mutateAsync: createSetupIntent, isPending: isCreatingSetupIntent } =
    useCreateSetupIntent();

  const {
    data: userSubscriptions,
    isLoading: isUserSubscriptionsLoading,
    error: userSubscriptionsError,
  } = useUserSubscriptions({
    enabled: isAuthenticated,
  });

  // Fetch plans from API
  const { data: plans, isLoading, error } = usePlans();

  const isHighlightedPlan = (plan: Subscription) => {
    if (isAgent) {
      return plan.name === "Growth Agent";
    } else {
      return plan.name === "Agency Pro";
    }
  };

  const getFilteredPlans = (): Subscription[] => {
    if (!plans) {
      console.log("No plans data available");
      return [];
    }
    const result = isAgent ? plans.agent : plans.agencies;
    return result;
  };

  const handleSelectPlan = async (plan: Subscription) => {
    // Don't allow subscription if user already has an active subscription
    if (userSubscriptions?.hasActiveSubscription) {
      return;
    }

    if (plan.price.amount) {
      try {
        // Create setup intent before navigating to payment
        if (!isAuthenticated || !account) {
          navigate("/sign-up");
          return;
        }

        const setupIntent = await createSetupIntent({
          accountId: account.id,
          customerEmail: account.email,
          customerName: account.name || "",
        });
        navigate("/payment", {
          state: {
            selectedPlan: plan,
            paymentIntent: setupIntent,
          },
        });
      } catch (error) {
        console.error("Failed to create setup intent:", error);
        alert("Failed to create subscription. Please try again.");
      }
    } else {
      navigate("/ContactUs");
    }
  };

  const handleAgentToggle = (newIsAgent: boolean) => {
    setIsAgent(newIsAgent);
  };

  // Show loading state for plans or user subscriptions
  if (isLoading || isUserSubscriptionsLoading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center pricing-loading">
        <div className="text-center">
          <div className="spinner-border pricing-spinner mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading plans...</p>
        </div>
      </div>
    );
  }

  // Show error state for plans or user subscriptions
  if (error || userSubscriptionsError) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center pricing-error">
        <div className="text-center">
          <p className="text-danger mb-3">Failed to load plans</p>
          <p className="text-muted">{"An error occurred"}</p>
        </div>
      </div>
    );
  }

  const filteredPlans = getFilteredPlans();

  if (!plans || filteredPlans.length === 0) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center pricing-empty">
        <div className="text-center">
          <p className="text-muted">
            No plans available for {isAgent ? "agents" : "agencies"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pricing-container">
      {/* Header */}
      <div className="container">
        <div className="text-center  ">
          <h1 className="display-4 fw-bold mb-3" style={{ color: "#0F0B0B" }}>
            Choose the right plan for your business
          </h1>
          <p className="lead text-muted mx-auto" style={{ maxWidth: "600px" }}>
            Simple, transparent pricing, no hidden fees
          </p>
          <AgentAgencySwitch isAgent={isAgent} onToggle={handleAgentToggle} />
        </div>

        {/* Pricing Cards */}
        <div className="row g-5 justify-content-center mt-5">
          {filteredPlans.map((plan: Subscription) => (
            <PricingCard
              key={plan.priceId}
              plan={plan}
              isHighlighted={isHighlightedPlan(plan)}
              onSelectPlan={handleSelectPlan}
              hasActiveSubscription={
                userSubscriptions?.hasActiveSubscription || false
              }
              isCreatingSetupIntent={isCreatingSetupIntent}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Pricing;
